/**
 * Super-Admin Tenants management — List, Create, Edit, Delete tenants.
 */
import React, { useEffect, useState } from 'react';
import {
  Button, Descriptions, Drawer, Form, Input, InputNumber,
  Modal, Popconfirm, Select, Space, Table, Tag, Typography, message, Tabs,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  ThunderboltOutlined, EyeOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import saApi from '../../services/superAdminApi';

const { Title, Text } = Typography;

interface Tenant {
  id: number;
  name: string;
  slug: string;
  domain: string | null;
  status: string;
  plan: string;
  owner_email: string;
  owner_phone: string | null;
  company_name: string | null;
  logo_url: string | null;
  primary_color: string;
  secondary_color: string;
  telegram_bot_token: string | null;
  telegram_bot_username: string | null;
  max_products: number;
  max_orders_per_month: number;
  max_admins: number;
  products_count: number;
  orders_this_month: number;
  admins_count: number;
  created_at: string;
}

const statusColors: Record<string, string> = {
  active: 'green',
  trial: 'gold',
  suspended: 'red',
  deleted: 'default',
};

const planColors: Record<string, string> = {
  free: 'default',
  starter: 'cyan',
  business: 'blue',
  enterprise: 'gold',
};

const SATenantsPage: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editDrawer, setEditDrawer] = useState<Tenant | null>(null);
  const [detailDrawer, setDetailDrawer] = useState<Tenant | null>(null);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const { data } = await saApi.get('/superadmin/tenants');
      setTenants(data);
    } catch (e) {
      message.error('Ошибка загрузки тенантов');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTenants(); }, []);

  const handleCreate = async (values: any) => {
    try {
      await saApi.post('/superadmin/tenants', values);
      message.success('Тенант создан');
      setCreateOpen(false);
      createForm.resetFields();
      fetchTenants();
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Ошибка создания');
    }
  };

  const handleUpdate = async (values: any) => {
    if (!editDrawer) return;
    try {
      await saApi.put(`/superadmin/tenants/${editDrawer.id}`, values);
      message.success('Тенант обновлён');
      setEditDrawer(null);
      fetchTenants();
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Ошибка обновления');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await saApi.delete(`/superadmin/tenants/${id}`);
      message.success('Тенант удалён');
      fetchTenants();
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const handleProvision = async (id: number) => {
    try {
      const { data } = await saApi.post(`/superadmin/tenants/${id}/provision`);
      message.success(data.message);
    } catch (e: any) {
      message.error(e.response?.data?.detail || 'Ошибка провизии');
    }
  };

  const openEdit = (t: Tenant) => {
    editForm.setFieldsValue({
      name: t.name,
      slug: t.slug,
      domain: t.domain,
      status: t.status,
      plan: t.plan,
      owner_email: t.owner_email,
      owner_phone: t.owner_phone,
      company_name: t.company_name,
      primary_color: t.primary_color,
      secondary_color: t.secondary_color,
      telegram_bot_token: t.telegram_bot_token,
      telegram_bot_username: t.telegram_bot_username,
      max_products: t.max_products,
      max_orders_per_month: t.max_orders_per_month,
      max_admins: t.max_admins,
    });
    setEditDrawer(t);
  };

  const columns: ColumnsType<Tenant> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 60,
    },
    {
      title: 'Название',
      dataIndex: 'name',
      render: (name: string, record) => (
        <a onClick={() => setDetailDrawer(record)}>{name}</a>
      ),
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      render: (slug: string) => <Text code>{slug}</Text>,
    },
    {
      title: 'Тариф',
      dataIndex: 'plan',
      width: 120,
      render: (plan: string) => (
        <Tag color={planColors[plan]}>{plan.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={statusColors[status]}>{status.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Владелец',
      dataIndex: 'owner_email',
      ellipsis: true,
    },
    {
      title: 'Товары',
      render: (_: any, r: Tenant) => `${r.products_count}/${r.max_products}`,
      width: 100,
    },
    {
      title: 'Создан',
      dataIndex: 'created_at',
      width: 120,
      render: (d: string) => dayjs(d).format('DD.MM.YYYY'),
    },
    {
      title: 'Действия',
      width: 180,
      render: (_: any, record: Tenant) => (
        <Space size="small">
          <Button size="small" icon={<EyeOutlined />} onClick={() => setDetailDrawer(record)} />
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Button size="small" icon={<ThunderboltOutlined />} title="Provision"
            onClick={() => handleProvision(record.id)} />
          <Popconfirm title="Удалить тенант?" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Тенанты</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchTenants}>Обновить</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            Создать тенант
          </Button>
        </Space>
      </div>

      <Table
        dataSource={tenants}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20 }}
        size="middle"
      />

      {/* ── Create Modal ── */}
      <Modal
        title="Новый тенант"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
        width={520}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="name" label="Название" rules={[{ required: true }]}>
            <Input placeholder="Pizza Planet" />
          </Form.Item>
          <Form.Item name="slug" label="Slug (URL)" rules={[{ required: true }]}>
            <Input placeholder="pizza-planet" addonBefore="https://" addonAfter=".foodplatform.ru" />
          </Form.Item>
          <Form.Item name="owner_email" label="Email владельца" rules={[{ required: true, type: 'email' }]}>
            <Input placeholder="owner@example.com" />
          </Form.Item>
          <Form.Item name="owner_phone" label="Телефон">
            <Input placeholder="+7 999 000 00 00" />
          </Form.Item>
          <Form.Item name="company_name" label="Компания">
            <Input placeholder='ООО "Пицца Планет"' />
          </Form.Item>
          <Form.Item name="plan" label="Тариф" initialValue="free">
            <Select options={[
              { value: 'free', label: 'FREE' },
              { value: 'starter', label: 'STARTER' },
              { value: 'business', label: 'BUSINESS' },
              { value: 'enterprise', label: 'ENTERPRISE' },
            ]} />
          </Form.Item>
          <Form.Item name="telegram_bot_token" label="Telegram Bot Token">
            <Input placeholder="123456:ABC-DEF..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Edit Drawer ── */}
      <Drawer
        title={`Редактировать: ${editDrawer?.name || ''}`}
        open={!!editDrawer}
        onClose={() => setEditDrawer(null)}
        width={520}
        extra={
          <Button type="primary" onClick={() => editForm.submit()}>Сохранить</Button>
        }
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdate}>
          <Tabs items={[
            {
              key: 'main',
              label: 'Основное',
              children: (
                <>
                  <Form.Item name="name" label="Название">
                    <Input />
                  </Form.Item>
                  <Form.Item name="slug" label="Slug">
                    <Input />
                  </Form.Item>
                  <Form.Item name="domain" label="Домен">
                    <Input placeholder="pizza-mario.ru" />
                  </Form.Item>
                  <Form.Item name="status" label="Статус">
                    <Select options={[
                      { value: 'active', label: 'Active' },
                      { value: 'trial', label: 'Trial' },
                      { value: 'suspended', label: 'Suspended' },
                    ]} />
                  </Form.Item>
                  <Form.Item name="plan" label="Тариф">
                    <Select options={[
                      { value: 'free', label: 'FREE' },
                      { value: 'starter', label: 'STARTER' },
                      { value: 'business', label: 'BUSINESS' },
                      { value: 'enterprise', label: 'ENTERPRISE' },
                    ]} />
                  </Form.Item>
                  <Form.Item name="owner_email" label="Email владельца">
                    <Input />
                  </Form.Item>
                  <Form.Item name="owner_phone" label="Телефон">
                    <Input />
                  </Form.Item>
                  <Form.Item name="company_name" label="Компания">
                    <Input />
                  </Form.Item>
                </>
              ),
            },
            {
              key: 'limits',
              label: 'Лимиты',
              children: (
                <>
                  <Form.Item name="max_products" label="Макс. товаров">
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item name="max_orders_per_month" label="Макс. заказов/мес">
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item name="max_admins" label="Макс. админов">
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>
                </>
              ),
            },
            {
              key: 'integrations',
              label: 'Интеграции',
              children: (
                <>
                  <Form.Item name="telegram_bot_token" label="Telegram Bot Token">
                    <Input.Password />
                  </Form.Item>
                  <Form.Item name="telegram_bot_username" label="Telegram Bot Username">
                    <Input addonBefore="@" />
                  </Form.Item>
                  <Form.Item name="primary_color" label="Основной цвет">
                    <Input type="color" style={{ width: 80 }} />
                  </Form.Item>
                  <Form.Item name="secondary_color" label="Вторичный цвет">
                    <Input type="color" style={{ width: 80 }} />
                  </Form.Item>
                </>
              ),
            },
          ]} />
        </Form>
      </Drawer>

      {/* ── Detail Drawer ── */}
      <Drawer
        title={`Тенант: ${detailDrawer?.name || ''}`}
        open={!!detailDrawer}
        onClose={() => setDetailDrawer(null)}
        width={480}
      >
        {detailDrawer && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="ID">{detailDrawer.id}</Descriptions.Item>
            <Descriptions.Item label="Название">{detailDrawer.name}</Descriptions.Item>
            <Descriptions.Item label="Slug"><Text code>{detailDrawer.slug}</Text></Descriptions.Item>
            <Descriptions.Item label="Домен">{detailDrawer.domain || '—'}</Descriptions.Item>
            <Descriptions.Item label="Статус">
              <Tag color={statusColors[detailDrawer.status]}>{detailDrawer.status.toUpperCase()}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Тариф">
              <Tag color={planColors[detailDrawer.plan]}>{detailDrawer.plan.toUpperCase()}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Владелец">
              {detailDrawer.owner_email}
              {detailDrawer.owner_phone && ` | ${detailDrawer.owner_phone}`}
            </Descriptions.Item>
            <Descriptions.Item label="Компания">{detailDrawer.company_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Товары">
              {detailDrawer.products_count} / {detailDrawer.max_products}
            </Descriptions.Item>
            <Descriptions.Item label="Заказы (мес)">
              {detailDrawer.orders_this_month} / {detailDrawer.max_orders_per_month}
            </Descriptions.Item>
            <Descriptions.Item label="Админы">
              {detailDrawer.admins_count} / {detailDrawer.max_admins}
            </Descriptions.Item>
            <Descriptions.Item label="Цвета">
              <Space>
                <div style={{ width: 24, height: 24, background: detailDrawer.primary_color, borderRadius: 4, border: '1px solid #d9d9d9' }} />
                <div style={{ width: 24, height: 24, background: detailDrawer.secondary_color, borderRadius: 4, border: '1px solid #d9d9d9' }} />
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="TG Bot">{detailDrawer.telegram_bot_username || '—'}</Descriptions.Item>
            <Descriptions.Item label="Создан">{dayjs(detailDrawer.created_at).format('DD.MM.YYYY HH:mm')}</Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </div>
  );
};

export default SATenantsPage;
